import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { SketchPicker, ColorResult } from 'react-color';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { EditIcon } from '../../../vectors/index';
import { useEditTemplate } from '../hooks/useEditTemplate';
import { EnterpriseTemplate } from '../types';

// react-color

type TemplateCompanyColorsModalProps = {
  isOpen: boolean;
  label: string;
  type: string;
  handleClose: () => void;
  formDataKey: string;
  defaultValue: string | null | undefined;
  templateData: EnterpriseTemplate;
  isBackground?: boolean;
  blurOpacityDefault?: number;
};

type FormData = {
  [key: string]: string;
};

// 🔹 Convert hex + opacity to 8-digit hex (#RRGGBBAA)
const hexWithOpacity = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = Math.round(alpha * 255);

  const toHex = (val: number) => {
    const hexVal = val.toString(16);
    return hexVal.length === 1 ? `0${hexVal}` : hexVal;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a)}`;
};

// 🔹 Parse #RRGGBBAA back to hex + opacity
const parseHexWithOpacity = (hex: string) => {
  if (hex.length === 9) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const a = parseInt(hex.slice(7, 9), 16) / 255;
    const hexColor = `#${hex.slice(1, 7)}`;
    return { hex: hexColor, alpha: a };
  }
  return { hex, alpha: 1 };
};

const TemplateCompanyColorsModal: FC<TemplateCompanyColorsModalProps> = ({
  templateData,
  label,
  type,
  isOpen,
  handleClose,
  formDataKey,
  defaultValue,
  isBackground = false,
  blurOpacityDefault,
}) => {
  const [errorText, setErrorText] = useState('');
  const [opacity, setOpacity] = useState(1);
  const [hexColor, setHexColor] = useState('#000000');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [blurOpacity, setBlurOpacity] = useState<number>(0);
  const { editTemplate, isUpdatingTemplate } = useEditTemplate({
    setErrorText,
  });

  const schema = yup.object().shape({
    [formDataKey]: yup.string(),
  });

  useEffect(() => {
    if (
      formDataKey === 'background_blur' &&
      typeof blurOpacityDefault === 'number'
    ) {
      setBlurOpacity(blurOpacityDefault);
    }
  }, [formDataKey, blurOpacityDefault]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    // resolver: yupResolver(schema),
    // @ts-ignore
    defaultValues: {
      [formDataKey]: defaultValue,
    },
  });

  const onSubmit = (values: FormData) => {
    let payload: Record<string, any> = {
      ...values,
      template_id: templateData?.id,
    };

    // ✅ Add blur_opacity separately if editing background_blur
    if (formDataKey === 'background_blur') {
      payload = {
        ...payload,
        blur_opacity: blurOpacity,
      };
    }

    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
        handleClose();
      },
    };

    editTemplate(payload, options);
    // editTemplate({ ...values, template_id: templateData?.id }, options);
  };

  const handleClickInput = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (color: ColorResult) => {
    setHexColor(color.hex);
    setOpacity(color.rgb.a ?? 1);

    // Save as #RRGGBBAA
    const finalHex = hexWithOpacity(color.hex, color.rgb.a ?? 1);
    setValue(formDataKey, finalHex);
  };

  const open = Boolean(anchorEl);

  // Parse initial value (#RRGGBBAA or #RRGGBB)
  useEffect(() => {
    if (defaultValue && isBackground) {
      const { hex, alpha } = parseHexWithOpacity(defaultValue);
      setHexColor(hex);
      setOpacity(alpha);
      setValue(formDataKey, hexWithOpacity(hex, alpha));
    } else if (defaultValue) {
      setValue(formDataKey, defaultValue || '#000000');
    }
  }, [defaultValue, formDataKey, setValue]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <EditIcon />
        <br />
        <Typography variant="h4">{label}</Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            spacing={3}
            sx={{
              pt: 3,
              '& p': {
                ml: 1,
                fontSize: '15px',
              },
            }}
          >
            {errorText && <Alert severity="error">{errorText}</Alert>}

            {isBackground ? (
              <Controller
                control={control}
                name={formDataKey}
                render={({ field }) => (
                  <div>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {label}
                    </Typography>

                    <Box
                      onClick={handleClickInput}
                      sx={{
                        width: '100%',
                        height: 40,
                        borderRadius: 1,
                        border: '1px solid #ccc',
                        backgroundColor: hexWithOpacity(hexColor, opacity),
                        cursor: 'pointer',
                      }}
                    />

                    {/* Popover with SketchPicker */}
                    <Popover
                      open={open}
                      anchorEl={anchorEl}
                      onClose={handleClosePopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                    >
                      <SketchPicker
                        color={{
                          r: parseInt(hexColor.slice(1, 3), 16),
                          g: parseInt(hexColor.slice(3, 5), 16),
                          b: parseInt(hexColor.slice(5, 7), 16),
                          a: opacity,
                        }}
                        onChange={handleColorChange}
                      />
                    </Popover>
                  </div>
                )}
              />
            ) : (
              <Controller
                control={control}
                name={formDataKey}
                render={({ field }) => (
                  <div>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {label}
                    </Typography>
                    <input
                      type="color"
                      {...field}
                      style={{
                        width: '100%',
                        height: '40px',
                        border: 'none',
                        padding: '0',
                        cursor: 'pointer',
                        borderRadius: '4px',
                      }}
                    />

                    {/* 👇 Opacity slider only if formDataKey is background_blur */}
                    {formDataKey === 'background_blur' && (
                      <Box mt={2}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Blur Opacity: {blurOpacity}%
                        </Typography>

                        <Slider
                          value={blurOpacity}
                          min={0}
                          max={100}
                          step={10}
                          onChange={(_, newValue) =>
                            setBlurOpacity(newValue as number)
                          }
                          valueLabelDisplay="auto"
                        />
                      </Box>
                    )}

                    {errors[formDataKey] && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {errors[formDataKey]?.message}
                      </Typography>
                    )}
                  </div>
                )}
              />
            )}
            <LoadingButton
              type="submit"
              variant="contained"
              size="small"
              fullWidth
              loading={isUpdatingTemplate}
              disabled={isUpdatingTemplate}
              loadingPosition="start"
              color="secondary"
              startIcon={<CheckIcon />}
              style={{
                marginTop: '10px',
                marginBottom: '10px',
                fontSize: '16px',
                padding: '7px 15px',
              }}
            >
              Save
            </LoadingButton>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCompanyColorsModal;
